import type { ReactNode } from "react";
import { Toast as ToastRadix } from "radix-ui";
import { icons, type IconName } from "../../icons";
import Button from "../Button/Button";
import IconButton from "../IconButton/IconButton";
import cx from "../../../../lib/cx.js";
import styles from "./Toast.css";

const iconSize = "24px";

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  return (
    <ToastRadix.Provider>
      {children}
      <ToastRadix.Viewport className={styles.viewport} />
    </ToastRadix.Provider>
  );
};

interface ToastProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  icon: IconName;
  /**
   * Label of the closing control: the visible button by default, the aria-label
   * of the X when `actions` takes that button's place.
   */
  closeLabel: string;
  duration?: number;
  /** Extra content below the description — e.g. a compact form. */
  children?: ReactNode;
  /**
   * Bottom-row controls (e.g. a submit button). Given, the header's closing
   * control becomes an X instead of the default labelled button, so the toast
   * still has exactly one way to dismiss it alongside swipe and Escape.
   */
  actions?: ReactNode;
  /**
   * `"neutral"` (the default) for a state the user should know about, like a
   * running pause; `"success"` to confirm that something the user did worked,
   * e.g. a saved form. A success toast sits on green and closes with the
   * solid white button instead of the outlined green one.
   */
  tone?: "neutral" | "success";
}

const Toast = ({
  open,
  onOpenChange,
  title,
  description,
  icon,
  closeLabel,
  duration,
  children,
  actions,
  tone = "neutral",
}: ToastProps) => {
  const Icon = icons[icon];

  return (
    <ToastRadix.Root
      className={cx(styles.root, tone === "success" && styles.success)}
      open={open}
      onOpenChange={onOpenChange}
      duration={duration}
    >
      <span className={styles.icon}>
        <Icon size={iconSize} />
      </span>
      <div className={styles.textBlock}>
        <ToastRadix.Title className={styles.title}>{title}</ToastRadix.Title>
        {description ? (
          <ToastRadix.Description className={styles.description}>
            {description}
          </ToastRadix.Description>
        ) : null}
      </div>
      <span className={styles.close}>
        {actions ? (
          // Radix's own Close, so the X, a swipe and Escape all end up in the
          // same `onOpenChange(false)`.
          <ToastRadix.Close asChild>
            <IconButton
              icon="x"
              ariaLabel={closeLabel}
              variant="tertiary"
              size="small"
            />
          </ToastRadix.Close>
        ) : (
          <Button
            variant={tone === "success" ? "primary" : "secondary"}
            color={tone === "success" ? "neutral" : "success"}
            size="small"
            title={closeLabel}
            onClick={() => onOpenChange(false)}
          />
        )}
      </span>
      {children ? <div className={styles.body}>{children}</div> : null}
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </ToastRadix.Root>
  );
};

export default Toast;
